/**
 * The verb-shaped wrapper the design's components were written against.
 *
 * Everything underneath is `../api` — one `fetch` with the versioned prefix,
 * the same-origin cookie, and the error class the rest of the platform already
 * throws. This file exists so that `api.get('/clients')` in a component copied
 * from the design repo means what it looks like it means, rather than being
 * rewritten into `api('/clients')` in forty places.
 *
 * The design repo's version of this file also echoed a CSRF cookie back as a
 * header. There is none here: this server's session cookie is `SameSite=Strict`
 * and every mutating route is under `/api/v1`, so the browser will not attach
 * it to a cross-site form post in the first place. Adding a token that nothing
 * checks would be a comment pretending to be a control.
 */

import { api as request, ApiError, type Options } from '../api'

export { ApiError }

type Body = Options['body']

export const api = {
  get: <T = unknown>(path: string, query?: Options['query']) =>
    request<T>(path, query === undefined ? {} : { query }),
  post: <T = unknown>(path: string, body?: Body) =>
    request<T>(path, body === undefined ? { method: 'POST' } : { method: 'POST', body }),
  patch: <T = unknown>(path: string, body?: Body) =>
    request<T>(path, body === undefined ? { method: 'PATCH' } : { method: 'PATCH', body }),
  delete: <T = unknown>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/**
 * What to put in front of somebody when a request fails.
 *
 * An `ApiError` already carries a sentence the server wrote for a person to
 * read. Anything else is the network, and saying so is more use than the
 * exception's own text, which is usually "Failed to fetch".
 */
export function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message
  return 'Could not reach the server. Check your connection.'
}
